import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  buildPhoneNumberSearchQuery,
  getPhoneNumberFilterDraftParts,
  updatePhoneNumberFilter,
} from './usePhoneNumberSearch.helpers';
import { useParseQuery } from '@/hooks/useParseQuery';
import { isSimCardsCategoryPath } from '@/features/PhoneNumberSearch/helpers';
import { getCurrentCategoryPath } from '@/hooks/useCurrentCategory/useCurrentCategory.helpers';
import { EMPTY_PHONE_NUMBER_FILTER } from '@/features/PhoneNumberSearch/constants';

export function usePhoneNumberSearch() {
  const { query, push, pathname } = useRouter();
  const { propertyOptions } = useParseQuery(true);
  const isSimCategory = isSimCardsCategoryPath(
    getCurrentCategoryPath(query.slug)
  );
  const urlFilter =
    (isSimCategory && propertyOptions.string[0]?.value) ||
    EMPTY_PHONE_NUMBER_FILTER;
  const [draft, setDraft] = useState(urlFilter);
  const syncedUrlFilterRef = useRef(urlFilter);

  if (!isSimCategory) {
    if (draft !== EMPTY_PHONE_NUMBER_FILTER) {
      setDraft(EMPTY_PHONE_NUMBER_FILTER);
    }
    syncedUrlFilterRef.current = EMPTY_PHONE_NUMBER_FILTER;
  } else if (urlFilter !== syncedUrlFilterRef.current) {
    syncedUrlFilterRef.current = urlFilter;
    setDraft(urlFilter);
  }

  const { operatorCode, subscriberDigits } =
    getPhoneNumberFilterDraftParts(draft);
  const canSubmit = draft !== EMPTY_PHONE_NUMBER_FILTER;
  const canReset =
    draft !== EMPTY_PHONE_NUMBER_FILTER ||
    urlFilter !== EMPTY_PHONE_NUMBER_FILTER;

  function setOperatorCode(nextOperatorCode: string | null) {
    setDraft(current =>
      updatePhoneNumberFilter(current, { operatorCode: nextOperatorCode })
    );
  }

  function setDigit(index: number, value: string) {
    setDraft(current =>
      updatePhoneNumberFilter(current, { subscriberDigit: { index, value } })
    );
  }

  function pushFilter(nextFilter: string, clearKeywords: boolean) {
    push(
      {
        pathname,
        query: buildPhoneNumberSearchQuery(query, nextFilter, clearKeywords),
      },
      undefined,
      { scroll: false }
    );
  }

  function handleSearch() {
    if (canSubmit) {
      pushFilter(draft, true);
    }
  }

  function handleReset() {
    setDraft(EMPTY_PHONE_NUMBER_FILTER);
    pushFilter(EMPTY_PHONE_NUMBER_FILTER, false);
  }

  return {
    isSimCategory,
    operatorCode,
    subscriberDigits,
    canSubmit,
    canReset,
    setOperatorCode,
    setDigit,
    handleSearch,
    handleReset,
  };
}
